// SPDX-License-Identifier: Elastic-2.0
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  applySystemUpdate,
  fetchSystemUpdates,
  type SystemUpdateStatus,
} from "@/api/systemUpdates";

// Check for updates at most once per hour — this is an ambient background
// poll, not a tight control loop. staleTime keeps the cached result alive
// so component remounts don't spam the manifest host.
const STALE_TIME_MS = 60 * 60 * 1000;
const REFETCH_INTERVAL_MS = 60 * 60 * 1000;

export interface UseSystemUpdatesResult {
  status: SystemUpdateStatus | undefined;
  isLoading: boolean;
  apply: () => void;
  isApplying: boolean;
  applyError: boolean;
}

export function useSystemUpdates(): UseSystemUpdatesResult {
  const query = useQuery<SystemUpdateStatus>({
    queryKey: ["system", "updates"],
    queryFn: fetchSystemUpdates,
    staleTime: STALE_TIME_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
    // Silent on error — update availability is ambient, never blocking.
    retry: 1,
  });

  const mutation = useMutation<{ status: string }, Error>({
    mutationFn: applySystemUpdate,
  });

  return {
    status: query.data,
    isLoading: query.isLoading,
    apply: () => mutation.mutate(),
    isApplying: mutation.isPending,
    applyError: mutation.isError,
  };
}
